import type { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';
import type {
    DmMessageFrustration,
    DmMessageInboxPage,
    DmMessagePatientView,
    DmMessageTriagePatch,
    DmMessageUrgency
} from '../services/dmMessagesInbox';
import { DM_MESSAGES_INBOX_KEY } from './queryKeys.ts';

interface TriageLevels {
    urgency: DmMessageUrgency;
    frustration: DmMessageFrustration;
    urgencyByAi: boolean;
    frustrationByAi: boolean;
}

export type TriageSnapshot = ReadonlyArray<readonly [QueryKey, unknown]>;

// Every cached query that shows this patient's triage: both list modes and the patient view.
export function triageQueryKeys(patientId: string): QueryKey[] {
    return [
        [DM_MESSAGES_INBOX_KEY, 'paged'],
        [DM_MESSAGES_INBOX_KEY, 'infinite'],
        [DM_MESSAGES_INBOX_KEY, 'patient', patientId]
    ];
}

// Frustration can be cleared, so only an absent field keeps the old value; null is a write.
// A written field is the staff's own from then on, so its AI marker goes.
export function mergeTriage<T extends TriageLevels>(triage: T, patch: DmMessageTriagePatch): T {
    const urgencyWritten = patch.urgency !== undefined;
    const frustrationWritten = patch.frustration !== undefined;
    return {
        ...triage,
        urgency: patch.urgency ?? triage.urgency,
        frustration: frustrationWritten ? patch.frustration : triage.frustration,
        urgencyByAi: urgencyWritten ? false : triage.urgencyByAi,
        frustrationByAi: frustrationWritten ? false : triage.frustrationByAi
    };
}

function patchPage(
    page: DmMessageInboxPage,
    patientId: string,
    patch: DmMessageTriagePatch
): DmMessageInboxPage {
    return {
        ...page,
        data: page.data.map((row) =>
            row.patientId === patientId ? { ...row, triage: mergeTriage(row.triage, patch) } : row
        )
    };
}

export function snapshotTriage(queryClient: QueryClient, patientId: string): TriageSnapshot {
    return triageQueryKeys(patientId).flatMap((queryKey) =>
        queryClient.getQueriesData({ queryKey })
    );
}

export function restoreTriage(queryClient: QueryClient, snapshot: TriageSnapshot): void {
    snapshot.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
}

export function applyOptimisticTriage(
    queryClient: QueryClient,
    patientId: string,
    patch: DmMessageTriagePatch
): void {
    const [pagedKey, infiniteKey, patientKey] = triageQueryKeys(patientId);
    queryClient.setQueriesData<DmMessageInboxPage>(
        { queryKey: pagedKey },
        (page) => page && patchPage(page, patientId, patch)
    );
    queryClient.setQueriesData<InfiniteData<DmMessageInboxPage>>(
        { queryKey: infiniteKey },
        (data) => data && { ...data, pages: data.pages.map((p) => patchPage(p, patientId, patch)) }
    );
    queryClient.setQueriesData<DmMessagePatientView>(
        { queryKey: patientKey },
        (view) =>
            view && {
                ...view,
                patient: { ...view.patient, triage: mergeTriage(view.patient.triage, patch) }
            }
    );
}
